import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { bootstrapConsentMode, updateGA4Consent } from '@/lib/tracking';
import { initSentry } from '@/lib/sentry';

export type ConsentStatus = 'granted' | 'denied' | null;

interface ConsentRecord {
  status: Exclude<ConsentStatus, null>;
  timestamp: string;
}

interface ConsentContextValue {
  status: ConsentStatus;
  timestamp: string | null;
  isPublicRoute: boolean;
  shouldShowBanner: boolean;
  accept: () => void;
  reject: () => void;
  revoke: () => void;
}

const STORAGE_KEY = 'traderisk_consent_v1';

const PRIVATE_ROUTE_PREFIXES = ['/app', '/admin', '/partner', '/login', '/signup', '/reset', '/auth'];

const isPrivatePath = (pathname: string): boolean =>
  PRIVATE_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const readStoredConsent = (): ConsentRecord | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.status !== 'granted' && parsed.status !== 'denied') return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeStoredConsent = (status: Exclude<ConsentStatus, null>): ConsentRecord => {
  const record: ConsentRecord = { status, timestamp: new Date().toISOString() };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage indisponível (modo privado restrito) — segue só em memória
  }
  return record;
};

const clearStoredConsent = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

export const ConsentProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const isPublicRoute = !isPrivatePath(location.pathname);

  const [record, setRecord] = useState<ConsentRecord | null>(() => readStoredConsent());

  // Status efetivo: em rota privada o usuário já consentiu via Termos no signup,
  // então tratamos como 'granted' independente do localStorage da LP.
  const effectiveStatus: ConsentStatus = isPublicRoute ? (record?.status ?? null) : 'granted';

  // Inicialização condicional de Sentry e atualização de consent do GA4.
  useEffect(() => {
    if (effectiveStatus === 'granted') {
      initSentry();
      updateGA4Consent(true);
    } else if (effectiveStatus === 'denied') {
      updateGA4Consent(false);
    }
  }, [effectiveStatus]);

  const accept = useCallback(() => {
    setRecord(writeStoredConsent('granted'));
  }, []);

  const reject = useCallback(() => {
    setRecord(writeStoredConsent('denied'));
  }, []);

  const revoke = useCallback(() => {
    clearStoredConsent();
    setRecord(null);
  }, []);

  const value: ConsentContextValue = {
    status: isPublicRoute ? (record?.status ?? null) : 'granted',
    timestamp: record?.timestamp ?? null,
    isPublicRoute,
    shouldShowBanner: isPublicRoute && record === null,
    accept,
    reject,
    revoke,
  };

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent deve ser usado dentro de <ConsentProvider>');
  }
  return ctx;
};

export { bootstrapConsentMode };
